import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, CalendarCheck } from "lucide-react";
import { api } from "@/lib/api";

const GREETING = "Hi! I'm Artical, your Wild Coffee concierge. Ask me about our tree-house cabins, experiences, or planning your stay in Negril.";

const mid = () => `m_${Math.random().toString(36).slice(2)}_${Date.now()}`;

// Internal site paths the assistant may mention (e.g. /book?cabin=canopy-suite&in=...&out=...)
const PATH_RE = /(\/(?:book|contact|cabins|experiences|gallery|story)(?:\?[A-Za-z0-9=&_:%-]+)?)/g;

function linkify(text, onNavigate) {
    const parts = [];
    let last = 0;
    let match;
    let i = 0;
    PATH_RE.lastIndex = 0;
    while ((match = PATH_RE.exec(text)) !== null) {
        if (match.index > last) parts.push(text.slice(last, match.index));
        const path = match[1];
        parts.push(
            <Link key={`l${i++}`} to={path} onClick={onNavigate} className="text-forest underline underline-offset-2 hover:text-terracotta font-medium break-all">{path}</Link>
        );
        last = match.index + path.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
}

function primaryBookLink(text) {
    const m = text.match(/\/book(?:\?[A-Za-z0-9=&_:%-]+)?/);
    return m ? m[0] : null;
}

function getSessionId() {
    let id = localStorage.getItem("artical_session");
    if (!id) {
        id = `s_${Math.random().toString(36).slice(2)}_${Date.now()}`;
        localStorage.setItem("artical_session", id);
    }
    return id;
}

export default function AssistantWidget() {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([{ id: "greeting", role: "assistant", content: GREETING }]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const scrollRef = useRef(null);
    const sessionId = useRef(getSessionId());

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    // Hide on admin/auth routes
    if (location.pathname.startsWith("/admin")) return null;

    const send = async () => {
        const text = input.trim();
        if (!text || loading) return;
        setInput("");
        setMessages((m) => [...m, { id: mid(), role: "user", content: text }]);
        setLoading(true);
        try {
            const r = await api.post("/assistant/chat", { session_id: sessionId.current, message: text });
            setMessages((m) => [...m, { id: mid(), role: "assistant", content: r.data.reply }]);
        } catch (e) {
            setMessages((m) => [...m, { id: mid(), role: "assistant", content: "Sorry, I'm having trouble right now — please try again in a moment, or use our Contact page." }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Launcher */}
            <button
                onClick={() => setOpen((o) => !o)}
                data-testid="assistant-launcher"
                aria-label="Open assistant"
                className="fixed bottom-5 right-4 sm:bottom-6 sm:right-6 z-[60] w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-forest text-bone shadow-xl flex items-center justify-center hover:bg-forestDark transition-all hover:scale-105"
            >
                {open ? <X size={22} /> : <MessageCircle size={22} />}
            </button>

            {/* Panel */}
            {open && (
                <div
                    data-testid="assistant-panel"
                    className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-[60] w-[92vw] max-w-sm h-[68vh] max-h-[540px] bg-bone border border-espresso/15 shadow-2xl flex flex-col overflow-hidden"
                >
                    <div className="bg-forestDark text-bone px-5 py-4 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-bone/15 flex items-center justify-center"><Sparkles size={16} className="text-gold" /></div>
                        <div>
                            <div className="font-serif text-lg leading-none">Artical Assistant</div>
                            <div className="text-[10px] uppercase tracking-widest2 text-bone/60 mt-1">Wild Coffee concierge</div>
                        </div>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="assistant-messages">
                        {messages.map((m) => (
                            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-forest text-bone" : "bg-bone2 text-ink border border-espresso/10"}`}>
                                    {m.role === "assistant" ? linkify(m.content, () => setOpen(false)) : m.content}
                                    {m.role === "assistant" && primaryBookLink(m.content) && (
                                        <Link
                                            to={primaryBookLink(m.content)}
                                            onClick={() => setOpen(false)}
                                            data-testid="assistant-reserve-cta"
                                            className="mt-2.5 flex w-max items-center gap-1.5 px-3 py-1.5 bg-forest text-bone text-[11px] uppercase tracking-widest2 hover:bg-forestDark transition-colors no-underline"
                                        >
                                            <CalendarCheck size={13} /> Reserve these dates
                                        </Link>
                                    )}
                                </div>
                            </div>
                        ))}
                        {loading && (
                            <div className="flex justify-start" data-testid="assistant-typing">
                                <div className="bg-bone2 border border-espresso/10 px-3.5 py-2.5 text-sm text-muted2">Artical is typing…</div>
                            </div>
                        )}
                    </div>

                    <div className="border-t border-espresso/15 p-3 flex items-center gap-2">
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                            placeholder="Ask about cabins, experiences…"
                            data-testid="assistant-input"
                            className="flex-1 border border-espresso/20 px-3 py-2.5 text-sm bg-transparent focus:border-forest outline-none"
                        />
                        <button onClick={send} disabled={loading || !input.trim()} data-testid="assistant-send" className="w-10 h-10 bg-forest text-bone flex items-center justify-center hover:bg-forestDark disabled:opacity-40">
                            <Send size={16} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
